import React, { useMemo } from 'react';
import { Select } from 'antd';
import { resolveFaIconClasses } from '../../utils/faIconUtils';
import { normalizeFollowUpActivity, normalizeFollowUpActivities } from '../../utils/followUpActivityUtils';

/** آیکون Font Awesome با استایل ثابت — در مودال، کارت و Select */
export const ActivityFaIcon = ({ icon, color = '#666', size = 14, className = '' }) => {
  const iconClasses = resolveFaIconClasses(icon);
  if (!iconClasses) {
    return (
      <span
        className="inline-block rounded-full flex-shrink-0"
        style={{ width: Math.max(8, size - 4), height: Math.max(8, size - 4), backgroundColor: color }}
        aria-hidden
      />
    );
  }

  return (
    <i
      className={`${iconClasses.className} follow-up-activity-fa-icon ${className}`.trim()}
      style={{
        color,
        fontSize: size,
        width: size + 4,
        minWidth: size + 4,
        textAlign: 'center',
        lineHeight: 1,
        fontFamily: iconClasses.brand ? '"Font Awesome 6 Brands"' : '"Font Awesome 6 Free"',
        fontWeight: iconClasses.brand ? 400 : 900,
        fontStyle: 'normal',
      }}
      aria-hidden
    />
  );
};

/** برچسب نوع فعالیت با آیکون Font Awesome */
export const ActivityTypeLabel = ({ activity, className = '', iconOnly = false }) => {
  const normalized = normalizeFollowUpActivity(activity);
  if (!normalized) return null;

  return (
    <span className={`inline-flex items-center gap-2 min-w-0 follow-up-activity-label ${className}`}>
      <ActivityFaIcon icon={normalized.icon} color={normalized.color || '#666'} size={14} />
      {!iconOnly && (
        <span className="truncate" style={{ fontFamily: 'CustomFont' }}>
          {normalized.name}
        </span>
      )}
    </span>
  );
};

/** نشان نوع فعالیت — بدون Tag آنت */
export const ActivityTypeBadge = ({ activity, className = '' }) => {
  const normalized = normalizeFollowUpActivity(activity);
  if (!normalized) return null;
  const color = normalized.color || '#1890ff';

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] follow-up-activity-badge ${className}`}
      style={{
        backgroundColor: `${color}18`,
        border: `1px solid ${color}44`,
        color,
        fontFamily: 'CustomFont',
      }}
    >
      <ActivityFaIcon icon={normalized.icon} color={color} size={13} />
      <span className="truncate">{normalized.name}</span>
    </span>
  );
};

const resolveActivity = (activityMap, id) =>
  activityMap.get(id) ??
  activityMap.get(Number(id)) ??
  activityMap.get(String(id));

/**
 * Select نوع فعالیت پیگیری — آیکون در لیست و مقدار انتخاب‌شده
 */
const FollowUpActivitySelect = ({ activities = [], loading, value, onChange, disabled, ...rest }) => {
  const normalizedActivities = useMemo(
    () => normalizeFollowUpActivities(activities),
    [activities],
  );

  const activityMap = useMemo(() => {
    const map = new Map();
    normalizedActivities.forEach((activity) => {
      map.set(activity.id, activity);
      map.set(Number(activity.id), activity);
      map.set(String(activity.id), activity);
    });
    return map;
  }, [normalizedActivities]);

  const options = useMemo(
    () =>
      normalizedActivities.map((activity) => ({
        value: activity.id,
        label: activity.name,
        title: activity.name,
      })),
    [normalizedActivities],
  );

  return (
    <Select
      placeholder="انتخاب نوع فعالیت"
      loading={loading}
      disabled={disabled}
      value={value}
      onChange={onChange}
      style={{ fontFamily: 'CustomFont', width: '100%' }}
      size="large"
      showSearch
      virtual={false}
      options={options}
      popupClassName="follow-up-activity-select-dropdown"
      filterOption={(input, option) =>
        String(option?.label ?? '')
          .toLowerCase()
          .includes(input.trim().toLowerCase())
      }
      optionRender={(option) => {
        const activity = resolveActivity(activityMap, option.value);
        return <ActivityTypeLabel activity={activity} />;
      }}
      labelRender={({ value: selectedValue }) => {
        const activity = resolveActivity(activityMap, selectedValue);
        if (activity) return <ActivityTypeLabel activity={activity} />;
        return <span style={{ fontFamily: 'CustomFont' }}>{selectedValue}</span>;
      }}
      {...rest}
    />
  );
};

export default FollowUpActivitySelect;
